import * as actionTypes from "../constants";
import * as apis from "../requests/apis";
import * as getPayload from "../requests/payloads";
import * as services from "../services";
import { clientRequest } from "../requests";
import { showToast } from "utils/toast";
import { TOAST_TYPE } from "utils/toast";
import { cognitoLogin, cognitoForgotPassword, cognitoConfirmPassword, cognitoSignOut } from "utils/cognito";

// Org/CreatePatient (and RegisterOrg) require a password even though the
// admin-created person completes their own registration via an emailed
// activation link, not this initial value — it's discarded once they set
// their own. Generated randomly rather than sent blank.
const generateTempPassword = () => {
  const bytes = new Uint8Array(18);
  window.crypto.getRandomValues(bytes);
  const random = Array.from(bytes, (b) => b.toString(36)).join("").slice(0, 20);
  // Guarantee at least one of each character class Cognito password policies
  // typically require, without relying on the random slice containing one.
  return `Aa1!${random}`;
};

// Shared by login() and register(): given an authenticated Cognito session,
// apply the same product/role access rules and either log the user in or
// reject with the matching toast. Returns true if the user ended up logged in.
const completeCognitoLogin = (cognitoUser, dispatch) => {
  const PRODUCT = process.env.REACT_APP_PRODUCT || "stroke";
  const adminOnlyRoles = ["superadmin", "brainberry_admin"];
  const isAdmin = adminOnlyRoles.includes(cognitoUser.role);

  if (PRODUCT === "brainberry") {
    // Admin build: only BrainBerry staff allowed
    if (!isAdmin) {
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: null });
      showToast("Access denied. This portal is for BrainBerry staff only.", TOAST_TYPE.error);
      return false;
    }
  } else {
    // Client portal: block admin-only accounts from logging in here
    if (isAdmin) {
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: null });
      showToast("Please use admin.cosmaapp.com to sign in with your admin account.", TOAST_TYPE.error);
      return false;
    }
    // Community accounts use the mobile app, not this portal
    if (cognitoUser.role === "community") {
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: null });
      showToast("Community accounts do not have portal access. Please use the COSMA app.", TOAST_TYPE.error);
      return false;
    }
  }

  const payload = {
    token: cognitoUser.token,
    userId: cognitoUser.userId,
    email: cognitoUser.email,
    userName: cognitoUser.userName,
    role: cognitoUser.role,
    product: cognitoUser.product || PRODUCT,
    customerId: cognitoUser.customerId,
    subscriptionId: cognitoUser.subscriptionId,
  };
  services.setLoginUser(payload);
  dispatch({ type: actionTypes.GET_USER_SUCCESS, payload });
  return true;
};

// Register
// "doctor"/"patient" form types are gone — that branch was unreachable dead
// code (the live registration UI only ever sets "community", "organisation",
// or defaults to single-user), and there's no REST equivalent for them
// anyway (doctors/patients are only created via Org/CreateDoctor+CreatePatient
// by an org admin, see redux/actions/auth.js's createDoctor/createPatient).
export const register = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.REGISTER_LOADING });
    const data = getPayload.registerPayload(form);

    if (form.type === "community")
      await clientRequest(actionTypes.POST, apis.REGISTER_API, data);
    else if (form.type === "organisation")
      await clientRequest(actionTypes.POST, apis.ORG_REGISTER_API, {
        email: data.email,
        password: data.password,
      });
    else
      await clientRequest(actionTypes.POST, apis.USER_REGISTER_API, data);

    // The Register/* endpoints don't return a session — this app
    // authenticates via Cognito directly, so log in with the same
    // credentials just submitted rather than relying on the REST response.
    const cognitoUser = await cognitoLogin(data.email, data.password);
    if (completeCognitoLogin(cognitoUser, dispatch)) {
      showToast("Registration successful.", TOAST_TYPE.success);
    }
  } catch (err) {
    const msg = err?.response?.data || err?.message || "Registration failed.";
    showToast(msg, TOAST_TYPE.error);
    dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
  }
};

// Login
export const login = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.LOGIN_LOADING });
    const cognitoUser = await cognitoLogin(form.email, form.password);
    if (completeCognitoLogin(cognitoUser, dispatch)) {
      showToast("You are successfully logged in.", TOAST_TYPE.success);
    }
  } catch (err) {
    if (err.code === "NewPasswordRequired") {
      showToast("Password reset required. Please contact support.", TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    } else if (err.code === "MFARequired") {
      showToast("MFA required. Please contact support.", TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    } else {
      const msg = err.message || "Login failed. Please check your credentials.";
      showToast(msg, TOAST_TYPE.error);
      dispatch({ type: actionTypes.GET_USER_FAIL, payload: err });
    }
  }
};

// Forget Password
export const forgetPassword = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FORGET_PASSWORD_LOADING });
    await cognitoForgotPassword(form.email);
    dispatch({ type: actionTypes.FORGET_PASSWORD_REQ_SUCCESS });
    showToast("Check your email for a verification code.", TOAST_TYPE.success);
  } catch (err) {
    showToast(err.message || "Failed to send reset email.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.FORGET_PASSWORD_REQ_FAIL, payload: err });
  }
};

// Reset Password (Cognito: requires email + confirmation code + new password)
export const resetPassword = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.RESET_PASSWORD_LOADING });
    await cognitoConfirmPassword(form.email, form.token, form.password);
    dispatch({ type: actionTypes.RESET_PASSWORD_SUCCESS });
    showToast("Password changed successfully.", TOAST_TYPE.success);
  } catch (err) {
    showToast(err.message || "Failed to reset password.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.RESET_PASSWORD_FAIL, payload: err });
  }
};

// Get User
export const getUser = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_USER });

    // Local-only: skip real Cognito/cookie auth entirely so the dashboards
    // render without logging in. No token is set, so any authenticated API
    // call will still fail — this is for viewing layout/UI, not real data.
    if (process.env.REACT_APP_DEV_BYPASS === "true") {
      dispatch({
        type: actionTypes.GET_USER_SUCCESS,
        payload: {
          token: null,
          userId: "dev-bypass",
          email: "dev-bypass@local.test",
          userName: "Dev Bypass",
          role: "user",
          product: process.env.REACT_APP_PRODUCT || "cog_app",
          customerId: null,
          subscriptionId: null,
        },
      });
      return;
    }

    const user = services.getLoginUser();
    if (user.userId) {
      // Portal is for end-users only — clear any stale superadmin/brainberry_admin session
      const portalBlockedRoles = ["superadmin", "brainberry_admin", "community"];
      if (portalBlockedRoles.includes(user.role)) {
        services.removeLoginUser();
        dispatch({ type: actionTypes.LOGOUT_SUCCESS });
        if (user.role === "community") {
          showToast("Community accounts do not have portal access. Please use the COSMA app.", TOAST_TYPE.error);
        } else {
          showToast("Please use admin.cosmaapp.com for admin accounts.", TOAST_TYPE.error);
        }
        return;
      }
      dispatch({
        type: actionTypes.GET_USER_SUCCESS,
        payload: user,
      });
    } else {
      dispatch({
        type: actionTypes.LOGOUT_SUCCESS,
      });
    }
  } catch (err) {
    dispatch({
      type: actionTypes.GET_USER_FAIL,
      payload: err,
    });
  }
};

// Logout
export const logout = () => async (dispatch) => {
  try {
    cognitoSignOut();
    services.removeLoginUser();
    dispatch({ type: actionTypes.GET_REPORT_CLEAR });
    dispatch({ type: actionTypes.LOGOUT_SUCCESS });
  } catch (err) {
    dispatch({ type: actionTypes.LOGOUT_FAIL, payload: err });
  }
};

// Create Org
// RegisterOrgRequest is {email, password} — no name field. The admin form
// never collected a password (only name+email), so one is generated here;
// the org sets their real password via the invite-activation email flow.
export const createOrg = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_ORG });
    const res = await clientRequest(actionTypes.POST, apis.CREATE_ORG_API, {
      email: form?.email,
      password: form?.password || generateTempPassword(),
    });
    showToast("Organisation successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_ORG_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to create organisation.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_ORG_FAIL, payload: err });
  }
};

// Create Doctor — CreateDoctorRequest {name, email, orgEmail}, same fields
// as before just camelCase.
export const createDoctor = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_DOCTOR });
    const res = await clientRequest(actionTypes.POST, apis.CREATE_DOCTOR_API, {
      name: form?.name,
      email: form?.email,
      orgEmail: form?.GetEmail || "",
    });
    showToast("Doctor successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_DOCTOR_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to add doctor.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_DOCTOR_FAIL, payload: err });
  }
};

// Create Patient — CreatePatientRequest wants {firstName, lastName, email,
// password, subscriptionId}, not the old single {Name, Email, SubscriptionId}.
// form.name is split naively on the first space since the form only ever
// collected one name field. password is generated, same reasoning as createOrg.
export const createPatient = (form) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CREATING_PATIENT });
    const [firstName, ...rest] = (form?.name || "").split(" ");
    const res = await clientRequest(actionTypes.POST, apis.CREATE_PATIENT_API, {
      firstName: firstName || "",
      lastName: rest.join(" "),
      email: form?.email,
      password: form?.password || generateTempPassword(),
      subscriptionId: form?.SubscriptionId || "",
    });
    showToast("Patient successfully added.", TOAST_TYPE.success);
    dispatch({ type: actionTypes.CREATE_PATIENT_SUCCESS, payload: res.data });
  } catch (err) {
    showToast(err?.response?.data || "Failed to add patient.", TOAST_TYPE.error);
    dispatch({ type: actionTypes.CREATE_PATIENT_FAIL, payload: err });
  }
};
